import React, { useEffect, useMemo, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { Button, Form, Icon, Table } from "semantic-ui-react";

type Row = {
  id: string;
  name: string;
  fee: string; // input value
  afterKey: string; // "head" | "pref:<n>" | "custom:<id>"
  sortKey?: number;
};

export type CustomShippingDestinationPayload = {
  id: string;
  name: string;
  fee: number | null;
  after_type: "head" | "prefecture" | "custom";
  after_value: number | string | null;
  // NOTE: 保存済みデータには sort_key が含まれるが、送信payloadとしては不要。
  sort_key?: number;
};

type Props = {
  customShippingDestinations?: CustomShippingDestinationPayload[];
  onChange: (
    customShippingDestinations: CustomShippingDestinationPayload[]
  ) => void;
};

const MAX_CUSTOM_SHIPPING_DESTINATIONS = 100;
const MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH = 50;

const PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
] as const;

const generateId = () => {
  // crypto.randomUUID が使えない環境向け
  // 形式: "<epoch_ms>_<random_hex>"（例: "1700000000123_a3f9c12b7e"）
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

// NOTE:
// - APIは位置情報を after_type / after_value で表現する（head / prefecture(n) / custom(id)）
// - UIではSelectのvalueとして扱いやすいよう、単一の文字列 afterKey にエンコードして保持する
//   - head: "head"
//   - prefecture: "pref:<n>"（末尾相当は "pref:<PREFECTURES.length>"）
//   - custom: "custom:<id>"
// - 表示時（parseRows）に payload → afterKey へ変換し、保存時（toPayload）に afterKey → payload へ戻す
const parseRows = (list: CustomShippingDestinationPayload[]): Row[] => {
  if (list.length === 0) return [];
  return (
    list
      .map((r: CustomShippingDestinationPayload, idx: number) => {
        // UIで未保存行も安定して扱うため、id が空なら一時IDを補完する
        const id =
          (r?.id ?? "").toString().trim() === "" ? generateId() : String(r.id);
        // name/fee は入力欄で扱いやすいよう文字列に揃える（feeは入力中に空文字を許容）
        const name = (r?.name ?? "").toString();
        const fee = r?.fee == null ? "" : r.fee.toString();

        // 位置情報は after_type / after_value を UI用の afterKey（Selectのvalue）に変換する
        // - head: "head"
        // - prefecture: "pref:<n>"（n は 1..PREFECTURES.length, "pref:<PREFECTURES.length>" が末尾相当）
        // - custom: "custom:<id>"
        const afterType = (r?.after_type ?? "").toString().trim();
        const afterValue = r?.after_value;

        const afterKey = (() => {
          if (afterType === "head") {
            return "head";
          }
          if (afterType === "prefecture") {
            const n = Number(afterValue);
            if (!Number.isFinite(n) || n <= 0 || n > PREFECTURES.length) {
              throw new Error("データの形式が不正です");
            }
            return `pref:${n}`;
          }
          if (afterType === "custom") {
            const id = String(afterValue ?? "").trim();
            if (!id) {
              throw new Error("データの形式が不正です");
            }
            return `custom:${id}`;
          }
          throw new Error("データの形式が不正です");
        })();

        // 保存済みデータの sort_key があれば並び順に利用する（なければ入力順）
        const sortKey =
          typeof r?.sort_key === "number" ? r.sort_key : undefined;

        return {
          id,
          name,
          fee,
          afterKey,
          sortKey,
          _idx: idx,
        };
      })
      // sort_key がある場合はそれを優先し、なければ入力順（_idx）で安定ソート
      .sort((a, b) => (a.sortKey ?? a._idx) - (b.sortKey ?? b._idx))
      // 内部用の _idx を取り除いて UI用 Row にする
      .map(({ _idx, ...r }) => r)
  );
};

const toPayload = (rows: Row[]): CustomShippingDestinationPayload[] => {
  // NOTE: 入力途中の行（name空など）もUI上は保持したいので、ここでは filter しない
  // 送信時に空行を除外する（呼び出し側で対応）
  return rows.map((r) => {
    // UI用の afterKey を API送信用の after_type / after_value に戻す（head/custom/prefの3系統）
    const afterKey = (r.afterKey || `pref:${PREFECTURES.length}`).toString();
    const [after_type, after_value] = (() => {
      if (afterKey === "head") return ["head", null] as const;

      if (afterKey.startsWith("custom:")) {
        const id = afterKey.slice("custom:".length).trim();
        if (!id) throw new Error("データの形式が不正です");
        return ["custom", id] as const;
      }

      if (afterKey.startsWith("pref:")) {
        const n = parseInt(afterKey.slice("pref:".length), 10);
        if (!Number.isFinite(n) || n <= 0 || n > PREFECTURES.length) {
          throw new Error("データの形式が不正です");
        }
        return ["prefecture", n] as const;
      }

      throw new Error("データの形式が不正です");
    })();

    // fee は空文字を null として扱い、数値化できない場合も null に倒す
    const parsedFee = r.fee === "" ? null : parseInt(r.fee, 10);
    const fee = parsedFee == null || Number.isNaN(parsedFee) ? null : parsedFee;

    return {
      // 念のため id が空なら補完（UI内で安定させる）
      id: (r.id || "").trim() === "" ? generateId() : r.id,
      // name は前後空白を除去し、上限文字数で丸める
      name: r.name.trim().slice(0, MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH),
      fee,
      after_type,
      after_value,
    };
  });
};

export const CustomShippingDestinationInputTable: React.FC<Props> = ({
  customShippingDestinations,
  onChange,
}) => {
  // NOTE: テーブル表示は幅が狭いと詰まりやすいので、1020px以下はカード（縦並び）へ切り替える
  const isNarrow = useMediaQuery({ maxWidth: 1020 });

  // 同一の parseError で alert が再レンダーのたびに連打されないよう、直近の表示内容を保持する
  const lastParseErrorRef = useRef<string | null>(null);
  const { rows, parseError } = useMemo(() => {
    try {
      return {
        rows: parseRows(customShippingDestinations ?? []),
        parseError: null as string | null,
      };
    } catch (e) {
      // 不正なデータでUIがクラッシュしないようにフォールバックする
      // （通知は useEffect で一度だけ出す）
      console.error(e);
      return {
        rows: [] as Row[],
        parseError:
          e instanceof Error && e.message
            ? e.message
            : "データの形式が不正です",
      };
    }
  }, [customShippingDestinations]);

  // parseRows の例外は useMemo 内で握りつつ、ユーザーへの通知は副作用として useEffect で行う
  // （render/useMemo 中に alert すると、再レンダーのたびに連打されやすいため）
  useEffect(() => {
    if (!parseError) return;
    if (lastParseErrorRef.current === parseError) return;
    lastParseErrorRef.current = parseError;
    alert(parseError);
  }, [parseError]);

  const update = (nextRows: Row[]) => {
    try {
      onChange(toPayload(nextRows));
    } catch (e) {
      console.error(e);
      alert("データの形式が不正です");
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="button"
          size="small"
          disabled={rows.length >= MAX_CUSTOM_SHIPPING_DESTINATIONS}
          onClick={() =>
            update([
              ...rows,
              {
                id: generateId(),
                name: "",
                fee: "",
                afterKey: `pref:${PREFECTURES.length}`,
              },
            ])
          }
        >
          追加
        </Button>
      </div>
      {isNarrow ? (
        <div>
          {rows.length === 0 ? (
            <div
              style={{
                border: "1px solid rgba(34, 36, 38, 0.15)",
                borderRadius: 4,
                padding: "0.75rem",
              }}
            >
              まだ登録されていません（右上の「追加」から作成できます）
            </div>
          ) : (
            rows.map((row, idx) => (
              <div
                key={row.id}
                style={{
                  border: "1px solid rgba(34, 36, 38, 0.15)",
                  borderRadius: 4,
                  padding: "0.75rem",
                  marginTop: "0.75rem",
                }}
              >
                <Form.Input
                  fluid
                  label={`配送先名(${MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH}文字まで)`}
                  value={row.name}
                  placeholder="例: 離島 / 福岡県南部"
                  input={{
                    maxLength: MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH,
                  }}
                  onChange={(_, data) => {
                    const next = [...rows];
                    next[idx] = {
                      ...next[idx],
                      name: String(data.value ?? "").slice(
                        0,
                        MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH
                      ),
                    };
                    update(next);
                  }}
                />
                <Form.Input
                  fluid
                  label="配送料"
                  value={row.fee}
                  placeholder="例: 1200"
                  type="number"
                  min="0"
                  onChange={(_, data) => {
                    const next = [...rows];
                    next[idx] = {
                      ...next[idx],
                      fee: (data.value ?? "") as string,
                    };
                    update(next);
                  }}
                />
                <Form.Select
                  fluid
                  label="位置"
                  value={row.afterKey}
                  options={(() => {
                    // 「先頭(head)」はひとつしか許容されない
                    const hasOtherHead = rows.some(
                      (r, i) => i !== idx && r.afterKey === "head"
                    );
                    const base = [
                      {
                        key: "head",
                        text: "先頭",
                        value: "head",
                        disabled: hasOtherHead && row.afterKey !== "head",
                      },
                      ...PREFECTURES.map((p, i) => ({
                        key: `pref:${i + 1}`,
                        text: `${p} の後`,
                        value: `pref:${i + 1}`,
                      })),
                    ];
                    const customs = rows
                      .filter((r, i) => i !== idx)
                      .filter(
                        (r) =>
                          (r.id || "").trim() !== "" || r.name.trim() !== ""
                      )
                      .map((r) => ({
                        key: `custom:${r.id}`,
                        text: `「${r.name.trim() || "（未入力）"}」の後`,
                        value: `custom:${r.id}`,
                      }));
                    return [...base, ...customs];
                  })()}
                  onChange={(_, data) => {
                    const next = [...rows];
                    next[idx] = {
                      ...next[idx],
                      afterKey: (data.value ??
                        `pref:${PREFECTURES.length}`) as string,
                    };
                    update(next);
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="button"
                    icon
                    size="small"
                    onClick={() => update(rows.filter((_, i) => i !== idx))}
                  >
                    <Icon name="trash" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <Table celled style={{ tableLayout: "fixed", width: "100%" }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: "45%" }}>
                配送先名({MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH}文字まで)
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: "15%" }}>
                配送料
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: "40%" }}>位置</Table.HeaderCell>
              <Table.HeaderCell collapsing style={{ width: 56 }} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="4">
                  まだ登録されていません（右上の「追加」から作成できます）
                </Table.Cell>
              </Table.Row>
            ) : (
              rows.map((row, idx) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <Form.Input
                      fluid
                      value={row.name}
                      placeholder="例: 離島 / 福岡県南部"
                      input={{
                        maxLength: MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH,
                      }}
                      onChange={(_, data) => {
                        const next = [...rows];
                        next[idx] = {
                          ...next[idx],
                          name: String(data.value ?? "").slice(
                            0,
                            MAX_CUSTOM_SHIPPING_DESTINATION_NAME_LENGTH
                          ),
                        };
                        update(next);
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Form.Input
                      fluid
                      value={row.fee}
                      placeholder="例: 1200"
                      type="number"
                      min="0"
                      onChange={(_, data) => {
                        const next = [...rows];
                        next[idx] = {
                          ...next[idx],
                          fee: (data.value ?? "") as string,
                        };
                        update(next);
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Form.Select
                      fluid
                      value={row.afterKey}
                      options={(() => {
                        const hasOtherHead = rows.some(
                          (r, i) => i !== idx && r.afterKey === "head"
                        );
                        const base = [
                          {
                            key: "head",
                            text: "先頭",
                            value: "head",
                            disabled: hasOtherHead && row.afterKey !== "head",
                          },
                          ...PREFECTURES.map((p, i) => ({
                            key: `pref:${i + 1}`,
                            text: `${p} の後`,
                            value: `pref:${i + 1}`,
                          })),
                        ];
                        const customs = rows
                          .filter((r, i) => i !== idx)
                          .filter(
                            (r) =>
                              (r.id || "").trim() !== "" || r.name.trim() !== ""
                          )
                          .map((r) => ({
                            key: `custom:${r.id}`,
                            text: `「${r.name.trim() || "（未入力）"}」の後`,
                            value: `custom:${r.id}`,
                          }));
                        return [...base, ...customs];
                      })()}
                      onChange={(_, data) => {
                        const next = [...rows];
                        next[idx] = {
                          ...next[idx],
                          afterKey: (data.value ??
                            `pref:${PREFECTURES.length}`) as string,
                        };
                        update(next);
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell
                    collapsing
                    textAlign="center"
                    style={{ width: 56 }}
                  >
                    <Button
                      type="button"
                      icon
                      size="mini"
                      onClick={() => update(rows.filter((_, i) => i !== idx))}
                    >
                      <Icon name="trash" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      )}
    </>
  );
};
